"use client";

import React, { useEffect, useState } from "react";
import { ImageViewer } from "../../../components/ImageViewer";
import { ModoHistoriaDto } from "../../../dto/modo-historia.dto";

interface PreguntaOficialDetailModalProps {
  pregunta: ModoHistoriaDto | null;
  isOpen: boolean;
  onClose: () => void;
  mode: "view" | "edit";
  onSave?: (pregunta: ModoHistoriaDto) => void;
}

const PreguntaOficialDetailModal: React.FC<PreguntaOficialDetailModalProps> = ({
  pregunta,
  isOpen,
  onClose,
  mode,
  onSave,
}) => {
  const [editData, setEditData] = useState<ModoHistoriaDto | null>(pregunta);
  const [isSaving, setIsSaving] = useState(false);

  // Actualizar editData cuando cambie la pregunta
  useEffect(() => {
    if (pregunta) {
      setEditData(pregunta);
    }
  }, [pregunta]);

  const handleSave = async () => {
    if (!editData || !onSave) return;

    setIsSaving(true);
    try {
      await onSave(editData);
      onClose();
    } catch (error) {
      console.error("Error al guardar:", error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !pregunta || !editData) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {mode === "edit" ? "Editar" : "Ver"} Pregunta Oficial
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              ID: {pregunta.idPreguntaOficial}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-6">
            {/* Pregunta */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pregunta
              </label>
              <div className="text-sm text-gray-900 bg-gray-50 px-4 py-3 rounded-md">
                {pregunta.contenido}
              </div>
            </div>

            {/* Imagen */}
            {pregunta.tieneImagen && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Imagen
                </label>
                <ImageViewer
                  imagen={pregunta.imagen}
                  tieneImagen={pregunta.tieneImagen}
                  className="mb-2"
                />
              </div>
            )}

            {/* Alternativas */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Alternativas
              </label>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(pregunta.alternativas).map(([key, value]) => (
                  <div key={key} className="bg-gray-50 px-3 py-2 rounded-md">
                    <span className="font-medium text-blue-600">{key}:</span>
                    <span className="ml-2 text-gray-900">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Respuesta correcta */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Respuesta Correcta
              </label>
              <div className="text-sm text-green-600 bg-green-50 px-3 py-2 rounded-md font-medium">
                {pregunta.respuesta}
              </div>
            </div>

            {/* Explicación */}
            {pregunta.explicacion && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Explicación
                </label>
                <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                  {pregunta.explicacion}
                </div>
              </div>
            )}

            {/* Datos Gráficos */}
            {pregunta.datosGrafico && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Datos Gráficos
                </label>
                <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                  <pre className="whitespace-pre-wrap font-mono text-xs">
                    {JSON.stringify(pregunta.datosGrafico, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {/* Información adicional */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Estado:</span>
                <span
                  className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                    pregunta.vigente
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {pregunta.vigente ? "Vigente" : "No Vigente"}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Revisada:</span>
                <span
                  className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                    pregunta.revisada
                      ? "bg-green-100 text-green-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {pregunta.revisada ? "Sí" : "No"}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Tiene Imagen:</span>
                <span
                  className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                    pregunta.tieneImagen
                      ? "bg-blue-100 text-blue-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {pregunta.tieneImagen ? "Sí" : "No"}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-700">
                  Datos Gráficos:
                </span>
                <span
                  className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                    pregunta.datosGrafico
                      ? "bg-purple-100 text-purple-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {pregunta.datosGrafico ? "Sí" : "No"}
                </span>
              </div>
            </div>

            {/* Checkbox de revisión en modo edición */}
            {mode === "edit" && (
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editData.revisada ?? false}
                    onChange={(e) =>
                      setEditData({ ...editData, revisada: e.target.checked })
                    }
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900">
                      Marcar como revisada
                    </span>
                    <p className="text-xs text-gray-600">
                      Marca esta casilla si has verificado que la pregunta está
                      completamente correcta
                    </p>
                  </div>
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {mode === "edit" ? "Cancelar" : "Cerrar"}
          </button>
          {mode === "edit" && (
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? "Guardando..." : "Guardar"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreguntaOficialDetailModal;
